
import PricePackageContainer from '@hokify/shared-components/lib/components/PricePackageContainer.vue';
import PersonCarousel from '@hokify/shared-components/lib/components/PersonCarousel.vue';
import { Component, Mixins } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import type { IAPIPackageVariant } from '@hokify/common';
import { getSupportPhone } from '@hokify/shared-components/lib/helpers/contact-info';
import { getAllAlternateUrls, getCanonicalUrl, getSocialMediaMetaData } from '~/helpers/metadata';
import LoginBase from '~/mixins/LoginBase';
import { getPaymentLogos } from '@hokify/shared-components/lib/helpers/logos';
import pricingFAQ from '~/data/pricing-faq.json';
import { ITrackWebsiteEvents } from '@hokify/tracking/types';
import BusinessTextBlockSection from '~/components/website/sections/business/BusinessTextBlockSection.vue';

const pricingStore = namespace('pricing');

@Component({
	name: 'PricingPage',
	layout: 'plain-company',
	components: {
		BusinessTextBlockSection,
		FAQ: () => import('@hokify/shared-components/lib/components/FAQ.vue'),
		ContactFormSection: () => import('~/components/website/sections/ContactFormSection.vue'),
		HokCarousel: () => import('@hokify/shared-components/lib/components/HokCarousel.vue'),
		PricePackageTable: () =>
			import('@hokify/shared-components/lib/components/PricePackageTable.vue'),
		PricePackageContainer,
		PersonCarousel
	},
	head() {
		const path = `${this.$store.state.host}/business/pricing`;

		const canonical = getCanonicalUrl(path);
		const alternates = getAllAlternateUrls(path);
		const result: any = {
			title: 'Stelle inserieren mit hokify - Unsere Preise & Angebote',
			link: [canonical, ...alternates],
			meta: [
				{ hid: 'robots', name: 'robots', content: 'index, follow' },
				{
					hid: 'description',
					name: 'description',
					content: 'Profitieren Sie von einem einmaligen Preis-Leistungs-Verhältnis.'
				}
			]
		};

		result.meta.push(
			...getSocialMediaMetaData(
				result,
				this.$store.state.topLevelDomain,
				undefined,
				undefined,
				this.$route.query
			)
		);
		return result;
	},
	async asyncData({ store }) {
		await store.dispatch('pricing/getPricelist', {
			region: store.state.topLevelDomain || 'at',
			type: 'job'
		});
	}
})
export default class PricingPage extends Mixins(LoginBase()) {
	@pricingStore.Getter('selectablePricelistPackages') selectablePricelistPackages;

	supportPhone = getSupportPhone(this.$store.state.topLevelDomain);

	paymentLogosPricing = getPaymentLogos('pricing');

	pricingFAQ = pricingFAQ;

	showBanner = this.$store.state.topLevelDomain === 'at';

	pricingDataOne = {
		image: 'hokify_stelleninserate_pricing_DE.png',
		imageMobile: 'hokify_stelleninserate_pricing_mobile_DE.png',
		alt: '',
		width: 293,
		height: 477
	};

	pricingDataTwo = {
		image: 'cost_per_application.png',
		imageMobile: 'cost_per_application_mobile.png',
		alt: '',
		width: 478,
		height: 478
	};

	goToCheckout() {
		this.deepLinkPath = '/checkout';
		window.location.href = this.redirectPath;
	}

	async mounted() {
		this.$trackWebsiteEvent('view_pricelist_packages', {
			packageVariants: this.selectablePricelistPackages
		});
	}

	handleBuyPackage(jobPackage: IAPIPackageVariant, contingent = 1) {
		this.$trackWebsiteEvent('select_package', {
			packageVariant: jobPackage,
			quantity: contingent
		});
		this.deepLinkPath = '/checkout';
		this.currentContingent = contingent;
		this.continueWithSignup(jobPackage);
	}

	continueWithSignup(jobPackage: IAPIPackageVariant) {
		this.cref = 'b2b';
		this.currentPackage = jobPackage;
		window.location.href = this.redirectPath;
	}

	arrangeWebinar() {
		this.$trackWebsiteEvent('arrange_hubspot_meeting', { pageElement: 'contact_us' });
		window.open(
			`https://hy.hokify.${this.$store.state.topLevelDomain}/meeting-vereinbaren`,
			'_blank'
		);
	}

	trackContingentSelection(contingent: number) {
		this.$trackWebsiteEvent('select_package_number', {
			packageCount: contingent
		});
	}

	showPricingModal(packageName: string) {
		this.$modal.show('tableModal');
		this.$trackWebsiteEvent('view_package_functionalities', {
			pageElement: packageName === 'noPackageSelected' ? `more_info_button` : 'package_cards',
			packageName
		});
	}

	trackViewFAQAnswer(index: number) {
		const mapIndexToLabel = [
			!this.isAtPage ? 'cost_per_application' : undefined,
			'difference_hygo_hyattract',
			'difference_hyattract_hyimpact',
			'metajob_platforms',
			'runtime'
		];

		if (mapIndexToLabel[index]) {
			this.$trackWebsiteEvent('view_answers', {
				pageElement: 'faq',
				elementContent: mapIndexToLabel[index]
			});
		}
	}

	trackViewTooltip(elementContent: string) {
		this.$trackWebsiteEvent('view_package_functionalities_details', {
			pageElement: 'package_details',
			elementContent
		});
	}

	modalTracking(action: keyof ITrackWebsiteEvents) {
		this.$trackWebsiteEvent(action, { pageElement: 'modal' });
		if (action === 'arrange_hubspot_meeting') {
			window.open(
				`https://hy.hokify.${this.$store.state.topLevelDomain}/meeting-vereinbaren`,
				'_blank'
			);
			this.$modal.hide('contactInfoModal');
		}
	}

	showModal(cta = false) {
		this.$modal.show('contactInfoModal');
		this.$trackWebsiteEvent('open_hubspot_meeting_modal', {
			interaction: cta ? 'cta' : 'more-than-10-jobs'
		});
	}

	scrollToContactSection(tracking?: boolean, element?: string) {
		this.$scrollTo('#more-info');
		if (tracking && element) {
			this.$trackWebsiteEvent('scroll_to_contact', { pageElement: element });
		}
	}

	getMoreInfos() {
		if (this.$isMobile.any) {
			this.$scrollTo('#pricing-top');
		} else {
			this.showPricingModal('noPackageSelected');
		}
	}

	get isAtPage(): boolean {
		return this.$store.state.topLevelDomain === 'at';
	}
}
